// ─────────────────────────────────────────────────────────────────────────
// UITRUSTING — bestelflow-delta's (batch-69, FO-512, spec 212)
//
// De bestelflow zelf (indienen, goedkeuren, verwerken met leverwijze per regel,
// credits, levering) bestaat al. Dit bestand bevat alleen de gedeelde bouwstenen
// van de delta's:
//
//   - de claim op de verwerking ("Oppakken", wie en sinds wanneer);
//   - extern bestellen geblokkeerd zolang er vrije voorraad is, met de
//     correctie-naar-nul als uitweg;
//   - de tijdelijke route "voorraadtelling in opbouw" (uit voorraad bij stand 0,
//     met einddatum en correctievoorstel);
//   - het verplichte minimumniveau bij "niet op voorraad";
//   - de status-tijdlijn die de aanvrager en de goedkeurders zien.
//
// Conventies als in uitrusting-verwerking.ts: nullable = `T | null`, datums als
// ISO-string over de wire, NL-kalenderdagen als yyyy-mm-dd.
// ─────────────────────────────────────────────────────────────────────────

import { z } from 'zod'
import type { UitrustingBestellingStatus } from './uitrusting'
import type {
  BestellingStap,
  UitrustingRegelStatus,
  UitrustingZendingStatus,
} from './uitrusting-verwerking'
import type { UitrustingVoorraadgevolg } from './uitrusting-inkoop'
import { UITRUSTING_REDEN_MIN } from './uitrusting-inkoop'
import { controleerArtikelNiveaus } from './voorraad'

// ─── Voorraadtelling in opbouw (tijdelijke route) ───────────────────────────

/**
 * Spec 212 A2: "tijdelijk" = een instelbare einddatum, standaard 31-12-2026. De
 * standaard staat hier (code), de keuze van beheer in `UitrustingInstellingen`;
 * een rij zonder keuze krijgt dus nooit stil ander gedrag dan een ontbrekende.
 */
export const VOORRAADTELLING_IN_OPBOUW_STANDAARD_TOT = '2026-12-31'

/** Kalender-geldige NL-dag (yyyy-mm-dd); "2026-02-31" rolt anders stil door. */
export function isNlKalenderDag(t: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(t)) return false
  const d = new Date(`${t}T00:00:00.000Z`)
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === t
}

/** PUT van de instelling: einddatum (leeg = de standaard) en de schakelaar. */
export const VoorraadtellingInstellingSchema = z.object({
  /** Laatste dag (yyyy-mm-dd, NL) waarop de route bestaat; null = de standaard. */
  tot: z
    .string()
    .trim()
    .refine(isNlKalenderDag, { message: 'Kies een geldige einddatum (dd-mm-jjjj)' })
    .nullable(),
  /** Bewust uit, ongeacht de datum. */
  uit: z.boolean(),
})
export type VoorraadtellingInstellingInput = z.infer<typeof VoorraadtellingInstellingSchema>

/** De stand van de tijdelijke route zoals scherm en API hem tonen. */
export interface VoorraadtellingStandDTO {
  /** Mag "uit voorraad bij stand 0" (al geleverd, geen mutatie) vandaag nog? */
  actief: boolean
  /** De effectieve einddatum (keuze of standaard), yyyy-mm-dd. */
  tot: string
  /** Is `tot` de standaard uit code (geen keuze van beheer)? */
  standaard: boolean
  /** Bewust uitgeschakeld. */
  uit: boolean
  gewijzigdOp: string | null
  gewijzigdDoorNaam: string | null
}

// ─── Claim op de verwerking ─────────────────────────────────────────────────

export const VerwerkingOppakkenSchema = z.object({
  /**
   * Een ander heeft de bestelling opgepakt: alleen met `overnemen` neem je hem
   * over (geauditeerd). Zonder vlag geeft de API 409 met de naam van de ander.
   */
  overnemen: z.boolean().optional(),
})
export type VerwerkingOppakkenInput = z.infer<typeof VerwerkingOppakkenSchema>

/** Wie de bestelling heeft opgepakt. */
export interface VerwerkingClaimDTO {
  opgepaktDoorId: string
  opgepaktDoorNaam: string | null
  opgepaktOp: string
  /** De ingelogde gebruiker heeft hem zelf opgepakt. */
  doorMij: boolean
}

// ─── Extern bij vrije voorraad: correctie naar nul ──────────────────────────

/**
 * "Voorraad corrigeren naar 0 (telling klopt niet)": de reden is verplicht, het
 * aantal niet (de API corrigeert altijd de hele vrije stand van de maat).
 */
export const VoorraadNaarNulSchema = z.object({
  reden: z
    .string()
    .trim()
    .min(
      UITRUSTING_REDEN_MIN,
      'Geef een reden van minimaal 5 tekens (bijvoorbeeld: vak is leeg, telling klopt niet)',
    )
    .max(1000),
  /**
   * De locaties waar de telling niet klopt (het vak is leeg). Alleen die worden
   * naar 0 gecorrigeerd; voorraad in bv. een servicebus blijft staan. Leeg
   * meesturen kan niet; weglaten = alle locaties met voorraad.
   */
  locatieIds: z
    .array(z.string().trim().min(1))
    .min(1, 'Kies minimaal één locatie waar de telling niet klopt')
    .max(100)
    .optional(),
})
export type VoorraadNaarNulInput = z.infer<typeof VoorraadNaarNulSchema>

export interface VoorraadNaarNulResultaatDTO {
  voorraadArtikelId: string
  /** Het aantal dat is afgeboekt (CORRECTIE_MIN over de gekozen locaties samen). */
  gecorrigeerd: number
  /** Open telverschillen die de telling "0" meteen verklaart (afwikkeling TELLING). */
  verschillenAfgewikkeld: number
  /** Wat er na de correctie nog vrij ligt op de andere locaties (blokkeert extern nog steeds). */
  restVrij: number
}

/** batch-69 (FO-512 §B.3) — de voorraad van een maat per locatie (correctie naar 0). */
export interface VoorraadLocatieStandDTO {
  locatieId: string
  locatieNaam: string
  /** MAGAZIJN | SERVICEBUS | KANTOOR | EXTERN */
  locatieType: string
  aantal: number
  /** Vrij = aantal min gereserveerd (nooit onder 0); dit deel corrigeert de correctie. */
  vrij: number
}

/**
 * Een andere bestelling of aanvraag heeft voorraad van deze maat gereserveerd:
 * dan blokkeert de correctie, met een verwijzing (spec 212 §5.3).
 */
export interface VoorraadReserveringVerwijzingDTO {
  aantal: number
  employeeNaam: string | null
  /** De uitrustingbestelling die het vasthoudt, als het er een is. */
  bestellingId: string | null
  /** Leesbare herkomst ("uitrustingbestelling", "gereedschapsaanvraag", ...). */
  herkomst: string
}

// ─── Minimumniveau bij "niet op voorraad" ───────────────────────────────────

/**
 * Per regel meegestuurd bij bevestigen als de voorraadbron nog geen bewust
 * vastgesteld minimum heeft. 0 is een geldige, bewuste keuze ("niet op voorraad
 * houden"). Het maximum is het aanvulniveau van de voorraadmodule (FO-521): nooit
 * onder het minimum, en verplicht bij een minimum boven 0. Dezelfde regels als
 * het niveau-schrijfpad van de voorraadmodule (`controleerArtikelNiveaus`); de
 * eis voor een bestelbaar artikel toetst de API op de bron zelf.
 * `maximumVoorraad` weglaten = het huidige maximum laten staan (alleen bij 0).
 */
export const MinimumNiveauKeuzeSchema = z
  .object({
    minimumVoorraad: z
      .number({ error: 'Vul een minimumniveau in (0 = bewust niet op voorraad houden)' })
      .int('Een minimumniveau is een heel aantal')
      .min(0, 'Een minimumniveau is 0 of meer')
      .max(100000, 'Maximaal 100.000'),
    maximumVoorraad: z
      .number({ error: 'Vul een maximum in (het aanvulniveau)' })
      .int('Een maximum is een heel aantal')
      .min(0, 'Een maximum is 0 of meer')
      .max(100000, 'Maximaal 100.000')
      .nullable()
      .optional(),
  })
  .superRefine((v, ctx) => {
    for (const fout of controleerArtikelNiveaus({
      minimumVoorraad: v.minimumVoorraad,
      maximumVoorraad: v.maximumVoorraad ?? null,
      bestelbaar: false,
      traceerbaar: false,
    })) {
      ctx.addIssue({ code: 'custom', path: [fout.veld], message: fout.melding })
    }
  })
export type MinimumNiveauKeuze = z.infer<typeof MinimumNiveauKeuzeSchema>

/** Het niveaubeleid van de voorraadbron van een regel (verwerkscherm). */
export interface MinimumNiveauStandDTO {
  voorraadArtikelId: string
  minimumVoorraad: number
  /** Het aanvulniveau (FO-521); null = niet ingesteld. */
  maximumVoorraad: number | null
  /** Besteleenheid: de basis van het voorgevulde maximum (`standaardMaximumVoorstel`). */
  bestelAantal: number | null
  /** Bestelbaar bulkartikel: dan zijn minimum (boven 0) en maximum allebei verplicht. */
  bestelbaar: boolean
  traceerbaar: boolean
  /**
   * Is het minimum bewust vastgesteld? Ja bij een minimum boven 0, of bij 0 als
   * de laatste vastlegging van de niveaus op 0 uitkwam (een minimum- of
   * maximumwijziging in de niveau-audit, of het vastleggen vanuit deze flow).
   * Nee = nooit ingesteld: dan vraagt bevestigen een minimum zodra de regel
   * "niet op voorraad" is.
   */
  vastgesteld: boolean
}

// ─── Status-tijdlijn ────────────────────────────────────────────────────────

/** De fase die de aanvrager en de goedkeurders zien. */
export const UITRUSTING_STATUS_FASES = [
  'CONCEPT',
  'INGEDIEND',
  'GOEDGEKEURD',
  'IN_VERWERKING',
  'VERWERKT',
  'GELEVERD',
  'AFGEKEURD',
  'GEANNULEERD',
] as const
export type UitrustingStatusFase = (typeof UITRUSTING_STATUS_FASES)[number]

export const UITRUSTING_STATUS_FASE_LABELS: Record<UitrustingStatusFase, string> = {
  CONCEPT: 'Concept',
  INGEDIEND: 'Ingediend, wacht op beoordeling',
  GOEDGEKEURD: 'Goedgekeurd, wacht op verwerking',
  IN_VERWERKING: 'In verwerking',
  VERWERKT: 'Verwerkt',
  GELEVERD: 'Geleverd',
  AFGEKEURD: 'Afgekeurd',
  GEANNULEERD: 'Geannuleerd',
}

/** De vaste stappen van de tijdlijn, in volgorde. */
/**
 * Eén stap op de voortgangsbalk. De stappen zelf (en hun labels) zijn die van
 * `bepaalBestellingStappen`: portal-lijst, portal-detail, verwerkscherm en de
 * status-tijdlijn tonen zo dezelfde voortgang met dezelfde woorden.
 */
export interface UitrustingTijdlijnStapDTO {
  stap: BestellingStap
  /** Het label (BESTELLING_STAP_LABELS), zodat elke weergave hetzelfde zegt. */
  label: string
  bereikt: boolean
  datum: string | null
  doorNaam: string | null
  /** Korte toelichting ("opgepakt door ...", "3 uit voorraad, 2 extern besteld"). */
  toelichting: string | null
}

/** Wat er met het extern bestelde deel van een regel is gebeurd. */
export interface UitrustingTijdlijnExternDTO {
  /** Moment dat de bestelmail is verstuurd; null = nog niet verstuurd. */
  besteldOp: string | null
  zendingStatus: UitrustingZendingStatus | null
  /** Alleen intern (backoffice): de leverancier blijft uit de portal. */
  leverancierNaam: string | null
  /** De gewenste leverdatum van de zending (yyyy-mm-dd of ISO). */
  verwachteLevering: string | null
}

export interface UitrustingTijdlijnRegelDTO {
  regelId: string
  omschrijving: string
  aantal: number
  regelStatus: UitrustingRegelStatus
  aantalUitVoorraad: number
  aantalExtern: number
  aantalGeleverd: number
  geleverdOp: string | null
  extern: UitrustingTijdlijnExternDTO | null
  /**
   * Al geleverd, verwerkt zonder bestelling (FO-467): het voorraadgevolg, of null.
   * Zo'n regel is niet extern besteld en niet uit voorraad klaargelegd.
   */
  alGeleverd: UitrustingVoorraadgevolg | null
}

export interface UitrustingTijdlijnGebeurtenisDTO {
  id: string
  /** De audit-actie (UitrustingBestellingAudit.action). */
  actie: string
  label: string
  datum: string
  doorNaam: string | null
  toelichting: string | null
}

export interface UitrustingStatusTijdlijnDTO {
  bestellingId: string
  status: UitrustingBestellingStatus
  fase: UitrustingStatusFase
  faseLabel: string
  /** True = de publieke weergave (portal): geen leveranciers, geen interne notities. */
  publiek: boolean
  claim: { doorNaam: string | null; op: string } | null
  stappen: UitrustingTijdlijnStapDTO[]
  regels: UitrustingTijdlijnRegelDTO[]
  /** Nieuwste eerst. */
  gebeurtenissen: UitrustingTijdlijnGebeurtenisDTO[]
}

/**
 * Leesbare labels voor de audit-acties van een uitrustingbestelling. Eén bron
 * voor het verwerkscherm, de status-tijdlijn in de backoffice en de portal.
 */
export const UITRUSTING_BESTELLING_AUDIT_LABELS: Record<string, string> = {
  INGEDIEND: 'Ingediend',
  GOEDGEKEURD: 'Goedgekeurd',
  AFGEKEURD: 'Afgekeurd',
  VERWERKT: 'Verwerkt',
  ZENDING_VERZONDEN: 'Bestelmail verzonden',
  ZENDING_MISLUKT: 'Bestelmail mislukt',
  ZENDING_OPNIEUW: 'Bestelmail opnieuw verzonden',
  REGEL_VERLAAGD: 'Aantal verlaagd',
  REGEL_GESCHRAPT: 'Regel geschrapt',
  REGEL_VARIANT_GEWISSELD: 'Andere variant gekozen',
  REGEL_GELEVERD: 'Geleverd gemeld',
  REGEL_GELEVERD_TERUG: 'Levering teruggedraaid',
  REGEL_RETOUR: 'Retour geregistreerd (credits terug)',
  GEANNULEERD: 'Geannuleerd',
  REGEL_ZONDER_BESTELLING: 'Al geleverd - verwerkt zonder bestelling',
  REGEL_DUBBEL_GESLOTEN: 'Dubbele aanvraag gesloten',
  REGEL_OPGESCHOOND: 'Afgesloten via de opschoonlijst',
  REGEL_ONTVANGEN: 'Ontvangen op kantoor, klaar voor overdracht',
  REGEL_ONTVANGST_TERUGGEDRAAID: 'Ontvangst op kantoor teruggedraaid',
  HERINDIENING_GEMAAKT: 'Herindiening gemaakt van een afgekeurde bestelling',
  OPNIEUW_INGEDIEND: 'Opnieuw ingediend als nieuwe bestelling',
  // batch-69 bestelflow (FO-512)
  OPGEPAKT: 'Opgepakt voor verwerking',
  OPGEPAKT_OVERGENOMEN: 'Verwerking overgenomen',
  VRIJGEGEVEN: 'Verwerking vrijgegeven',
  VOORRAAD_NAAR_NUL: 'Voorraad gecorrigeerd naar 0 (telling klopte niet)',
  MINIMUM_VASTGELEGD: 'Minimumniveau vastgelegd',
  CORRECTIE_BEVESTIGD: 'Voorraadcorrectie bevestigd (stand was 0)',
}

/**
 * Acties die de AANVRAGER (portal) in zijn tijdlijn ziet. De rest is intern
 * werk (mailfouten, voorraadcorrecties, minimumniveaus, claims overnemen) en
 * zegt de medewerker niets over waar zijn spullen zijn.
 */
export const UITRUSTING_PUBLIEKE_AUDIT_ACTIES: ReadonlySet<string> = new Set([
  'INGEDIEND',
  'GOEDGEKEURD',
  'AFGEKEURD',
  'OPGEPAKT',
  'VERWERKT',
  'REGEL_GESCHRAPT',
  // Wat de aanvrager uiteindelijk krijgt, verandert hierdoor (zonder de interne
  // reden; de tijdlijn noemt het artikel).
  'REGEL_VERLAAGD',
  'REGEL_VARIANT_GEWISSELD',
  'REGEL_RETOUR',
  'REGEL_GELEVERD',
  'REGEL_ZONDER_BESTELLING',
  'REGEL_ONTVANGEN',
  'GEANNULEERD',
  'OPNIEUW_INGEDIEND',
  'HERINDIENING_GEMAAKT',
])

// ─── Melding aan de goedkeurder ─────────────────────────────────────────────

/** De twee momenten waarop de goedkeurder een compacte statusmelding krijgt. */
export const UITRUSTING_GOEDKEURDER_FASES = ['EXTERN_BESTELD', 'GELEVERD'] as const
export type UitrustingGoedkeurderFase = (typeof UITRUSTING_GOEDKEURDER_FASES)[number]
